// MST
import { types, flow, cast } from 'mobx-state-tree'

import {
  ICertificationRequirementGetAllAttrs,
  ICertificationRequirementGetAttrs,
  ICertificationRequirementSaveAttrs,
} from 'interfaces/back-office/content-management/certification/requirement/certification-requirement-view.interface'
import {
  ICertificationRequirementApiGetAllResponse,
  ICertificationRequirementApiGetResponse,
  ICertificationRequirementApiPaginatedResponse,
  ICertificationRequirementApiSaveResponse,
} from 'interfaces/back-office/content-management/certification/requirement/certification-requirement-api.interface'
import { ICertificationRequirementDetail } from 'interfaces/back-office/content-management/certification/requirement'

// Extensions
import { withEnvironment } from 'models/extensions/with-environment'
import { withHelper_baseResponsePaginationTypes } from 'models/extensions/with-helper'

// Lodash
import omit from 'lodash/omit'

// Utils
import { dateUtils_formatDate } from 'utils/common/date.utils'
import { commonUtils_renderTableNumber } from 'utils/common/common.utils'

// Certification Requirement Props
const certificationRequirementProps = {
  number: types.maybeNull(types.number),
  id: types.maybeNull(types.number),
  title: types.maybeNull(types.string),
  content: types.maybeNull(types.string),
  status: types.maybeNull(types.boolean),
  createdAt: types.maybeNull(types.string),
  updatedAt: types.maybeNull(types.string),
}

// Certification Requirement List Model
const CertificationRequirements = types
  .model('CertificationRequirements')
  .props(withHelper_baseResponsePaginationTypes(types.model('CertificationRequirementList').props(certificationRequirementProps)))

// Certification Requirement Detail Model
const CertificationRequirementDetail = types
  .model('CertificationRequirementDetail')
  .props(omit(certificationRequirementProps, ['number']))

const CertificationRequirementStoreModel = types
  .model('CertificationRequirement')
  .props({
    certificationRequirement_isLoading: types.optional(types.boolean, false),
    certificationRequirement_isSaveLoading: types.optional(types.boolean, false),
    certificationRequirement_certificationRequirements: types.optional(CertificationRequirements, {}),
    certificationRequirement_detail: types.optional(CertificationRequirementDetail, {}),
  })
  .actions(self => ({
    certificationRequirement_SET_LOADING: (payload: boolean): void => {
      self.certificationRequirement_isLoading = payload
    },
    certificationRequirement_SET_SAVE_LOADING: (payload: boolean): void => {
      self.certificationRequirement_isSaveLoading = payload
    },
    certificationRequirement_SET_CERTIFICATION_REQUIREMENTS: (payload: ICertificationRequirementApiPaginatedResponse): void => {
      const mapPayload = {
        ...payload,
        list: payload.list.map((list, index) => {
          return {
            ...list,
            number: commonUtils_renderTableNumber(index, payload),
            createdAt: dateUtils_formatDate(list.createdAt!, { tableFormat: true }),
            updatedAt: dateUtils_formatDate(list.updatedAt!, { tableFormat: true }),
          }
        }),
      }
      self.certificationRequirement_certificationRequirements = cast(mapPayload)
    },
    certificationRequirement_SET_CERTIFICATION_REQUIREMENT: (payload: ICertificationRequirementDetail): void => {
      self.certificationRequirement_detail = cast(payload)
    },
    certificationRequirement_CLEAR_CERTIFICATION_REQUIREMENTS: (): void => {
      self.certificationRequirement_certificationRequirements = cast({})
    },
    certificationRequirement_CLEAR_CERTIFICATION_REQUIREMENT: (): void => {
      self.certificationRequirement_detail = cast({})
    },
  }))
  .extend(withEnvironment)
  .actions(self => ({
    /**
     * @description Fetch certificationRequirements
     *
     * @param {ICertificationRequirementGetAllAttrs} payload
     *
     * @return {any} any
     */
    certificationRequirement_fetchCertificationRequirements: flow(function* (payload: ICertificationRequirementGetAllAttrs) {
      self.certificationRequirement_SET_LOADING(true)

      const response: ICertificationRequirementApiGetAllResponse =
        yield self.environment.certificationRequirementApi.certificationRequirement_getCertificationRequirements({
          body: { ...payload.body },
          requestConfig: { ...payload.requestConfig },
        })
      if (response.ok) self.certificationRequirement_SET_CERTIFICATION_REQUIREMENTS(response.res.data)

      self.certificationRequirement_SET_LOADING(false)
    }),

    /**
     * @description Fetch certification requirement
     *
     * @param {ICertificationRequirementGetAttrs} payload
     *
     * @return {any} any
     */
    certificationRequirement_fetchCertificationRequirement: flow(function* (payload: ICertificationRequirementGetAttrs) {
      self.certificationRequirement_SET_LOADING(true)

      const response: ICertificationRequirementApiGetResponse =
        yield self.environment.certificationRequirementApi.certificationRequirement_getCertificationRequirement({
          params: { id: payload.params.id },
          requestConfig: { ...payload.requestConfig },
        })
      if (response.ok) {
        self.certificationRequirement_SET_CERTIFICATION_REQUIREMENT(response.res.data)

        // Do when response success
        if (payload?.onSuccess) payload.onSuccess()
      }

      if (!response.ok) {
        // Do when response error
        if (payload?.onError) payload.onError()
      }

      self.certificationRequirement_SET_LOADING(false)
    }),

    /**
     * @description Save certification requirement
     *
     * @param {ICertificationRequirementSaveAttrs} payload
     *
     * @return {any} any
     */
    certificationRequirement_saveCertificationRequirement: flow(function* (payload: ICertificationRequirementSaveAttrs) {
      self.certificationRequirement_SET_SAVE_LOADING(true)
      const response: ICertificationRequirementApiSaveResponse =
        yield self.environment.certificationRequirementApi.certificationRequirement_saveCertificationRequirement({
          params: { ...payload.params },
          body: { ...payload.body },
          requestConfig: { ...payload.requestConfig },
        })
      if (response.ok) {
        // Do when response success
        if (payload?.onSuccess) payload.onSuccess()
      }

      self.certificationRequirement_SET_SAVE_LOADING(false)
    }),
  }))

export const createCertificationRequirementStoreDefaultModel = () => types.optional(CertificationRequirementStoreModel, {})
